/**
 * Router. /login is full-page; everything else routes through WorkspaceLayout
 * and resolves via the view registry.
 */
import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import RequireAuth from '@/components/RequireAuth'
import { WorkspaceLayout } from '@/components/workspace/WorkspaceLayout'
import { AppShell } from '@/components/layout/AppShell'

const Login = lazy(() => import('@/pages/Login'))
const BacktestDetail = lazy(() => import('@/pages/BacktestDetail'))
const PlanDetail = lazy(() => import('@/pages/PlanDetail'))
const StrategyDetail = lazy(() => import('@/pages/StrategyDetail'))
const TrackerDetail = lazy(() => import('@/pages/TrackerDetail'))
const JournalDetail = lazy(() => import('@/pages/JournalDetail'))
const IncomeCycles = lazy(() => import('@/pages/IncomeCycles'))

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground font-mono text-sm">
      Loading…
    </div>
  )
}

function CockpitRedirect() {
  const { tradeId } = useParams()
  return <Navigate to={tradeId ? `/tracker/${tradeId}` : '/tracker'} replace />
}

export default function AppRouter() {
  return (
    <Routes>
      <Route
        path="/login"
        element={<Suspense fallback={<PageLoader />}><Login /></Suspense>}
      />
      <Route element={<RequireAuth />}>
        {/* Dedicated full-page detail routes — matched before the workspace
            catch-all so a click opens its own URL with a back-to-list. Wrapped
            in AppShell so they keep the sidebar + header chrome. */}
        <Route element={<AppShell />}>
          <Route path="/backtests/:runId"
            element={<Suspense fallback={<PageLoader />}><BacktestDetail /></Suspense>} />
          <Route path="/plans/:planId"
            element={<Suspense fallback={<PageLoader />}><PlanDetail /></Suspense>} />
          <Route path="/strategies/:strategyId"
            element={<Suspense fallback={<PageLoader />}><StrategyDetail /></Suspense>} />
          <Route path="/tracker/:tradeId"
            element={<Suspense fallback={<PageLoader />}><TrackerDetail /></Suspense>} />
          <Route path="/journal/:tradeId"
            element={<Suspense fallback={<PageLoader />}><JournalDetail /></Suspense>} />
          <Route path="/income/:granularity"
            element={<Suspense fallback={<PageLoader />}><IncomeCycles /></Suspense>} />
          {/* The cockpit merged into the trade page (2026-07-31) — both views
              read the same payload and overlapped on all but a few sections.
              Redirect so existing links and bookmarks keep working. */}
          <Route path="/cockpit/:tradeId" element={<CockpitRedirect />} />
        </Route>
        {/* All other authed paths render the workspace shell.
            URL ↔ focused panel sync is handled inside WorkspaceLayout. */}
        <Route path="/*" element={<WorkspaceLayout />} />
      </Route>
    </Routes>
  )
}
